import {products} from "./feature.products.svelte.js";
import {ENTRY_DURATION_2_HOUR, ENTRY_DURATION_DAY, ENTRY_DURATION_EVENING, ENTRY_DURATION_MORNING} from "../../AppConstants";
import AppConfig from "../../AppConfig.js";
import * as products_select from './products.scenarios.select.svelte.js';
import DateTimeUtil from "../../utils/DateTimeUtil.js";
import * as productsets_select from '../productsets/productsets.scenarios.select.svelte.js';

export function getProductByID(iProductID)
{
  iProductID = parseInt(iProductID);
  const aProducts = products.lItems;

  let oFoundProduct = aProducts.find((oProduct) => {
    return oProduct.iID === iProductID;
  });

  return oFoundProduct || false;
}

/**
 * Frontend display title for a product. Prefers the website-specific title
 * (sTitleIfDuo, the catalogue's TiDuo) and falls back to the regular title.
 * Single source of truth so every product card / dialog shows the same title.
 * Mirrors the PHP Product::getFrontendTitle() used on the SilverStripe site.
 *
 * @param {number|string} iProductID
 * @returns {string}
 */
export function getFrontendTitleByID(iProductID)
{
  const oProduct = getProductByID(iProductID);
  if ( !oProduct )
  {
    return '';
  }

  return oProduct.sTitleIfDuo || oProduct.sTitle || '';
}

export function getProductInfoFromAvailableTreatmentsByID(iProductID)
{
  iProductID = parseInt(iProductID);
  const aAvailableTreatments = products.lAvailableTreatments;
// console.log('floep:',$state.snapshot(products));
  if ( !aAvailableTreatments || aAvailableTreatments.length === 0 )
  {
    return false;
  }

  const iSubCatIndex = aAvailableTreatments.findIndex((oSubCat) => {
    return oSubCat.lItems.some((oItem) => oItem.iID === iProductID);
  });

  const oSubCat = aAvailableTreatments[iSubCatIndex];

  if ( oSubCat )
  {
    return oSubCat.lItems.find((oItem) => oItem.iID === iProductID) || false;
  }

  const oCatalogueProduct = getProductByID(iProductID);
  const oAvailability = products.oAvailabilityByProductID[iProductID];
  if ( oCatalogueProduct && oAvailability )
  {
    return {...oCatalogueProduct, ...oAvailability};
  }

  return false;
}

/**
 * Exact required number of people for a multi-person treatment (0 = no constraint).
 * Sourced from the catalogue item (set in importFromPageConfigAjaxCall).
 */
export function getRequiredPeopleForProduct(iProductID)
{
  const oProduct = getProductByID(iProductID);
  return (oProduct && oProduct.iNrOfPeopleExact) || 0;
}

/**
 * Whether a shared timeslot must be chosen for this product (multi-person treatments).
 */
export function isTimeSelectionRequiredForProduct(iProductID)
{
  const oProduct = getProductByID(iProductID);
  return !!(oProduct && oProduct.bIsTimeSelectionRequired);
}

/**
 * True if this product has no exact-people constraint, or Elli reports at least the required
 * number of spots in some timeslot (iSpotsAvailable, set in the Phase 2 merge for flagged
 * products). Best-effort card pre-filter — Step C does the authoritative set fetch. If the spot
 * signal hasn't loaded yet, don't hard-block.
 */
export function hasEnoughSpotsForRequiredPeople(iProductID)
{
  const iRequired = getRequiredPeopleForProduct(iProductID);
  if ( iRequired <= 1 ) return true;

  const oExtra = getProductInfoFromAvailableTreatmentsByID(iProductID);
  if ( !oExtra || typeof oExtra.iSpotsAvailable === 'undefined' ) return true;

  return oExtra.iSpotsAvailable >= iRequired;
}

/**
 * Moet dit product als losse card renderen? Nee als het lid is van een persisted ProductSet
 * (de set-card vertegenwoordigt het dan). De oude RelatedProduct/iVariantOf-variantcheck is
 * vervallen: sets komen volledig uit de ProductSet-descriptors.
 */
export function shouldRenderProductStandalone(iProductID)
{
  return !productsets_select.isProductInPersistedSet(iProductID);
}

/**
 * Resolve the productset for an anchor product: its set ID, type, title, main product, the
 * ordered member IDs and the combined duration label.
 *
 * Persisted sets resolven op hun expliciete ProductSet-ID. Zonder ID (of onbekende ID) is dit
 * een virtuele set van één: [anchor]. De oude RelatedProduct-variantpartner-fallback is
 * verwijderd — losse producten (die dit pad raken) hebben per definitie geen partner meer.
 *
 * Single source of truth for "what is this card's set", shared by the card render and the
 * canonical Toevoegen action (openProductAddFlow), so displayed set and added set never diverge.
 */
export function getProductsetForAnchorID(iAnchorID, iProductSetID = 0)
{
  iAnchorID = parseInt(iAnchorID);
  iProductSetID = parseInt(iProductSetID, 10) || 0;

  if ( iProductSetID > 0 )
  {
    const oPersistedProductSet = productsets_select.getProductSetByID(iProductSetID);
    if ( oPersistedProductSet )
    {
      return {
        iProductSetID: oPersistedProductSet.iID,
        sType: oPersistedProductSet.sType,
        sTitle: oPersistedProductSet.sTitle,
        iMainProductID: oPersistedProductSet.iMainProductID,
        aProductIDs: [...oPersistedProductSet.aProductIDs],
        sDurationLabel: productsets_select.getDurationLabelForProductSet(oPersistedProductSet)
      };
    }
  }

  // Virtuele set van één product: geen persisted set (of onbekende ID).
  const iAnchorDuration = getProductByID(iAnchorID)?.iDuration || 0;

  return {
    iProductSetID: 0,
    sType: '',
    sTitle: getFrontendTitleByID(iAnchorID),
    iMainProductID: iAnchorID,
    aProductIDs: [iAnchorID],
    sDurationLabel: iAnchorDuration ? `${iAnchorDuration} min.` : ''
  };
}

export function getAvailableTimeSlotsForProduct(iProductID, iForXNrOfPeople = 1)
{
  // Bij elke mutatie van de booking wordt booking.oBookingSummary
  // bijgewerkt met daarin oProductPeopleCount
  // Die kunnen we dus veilig gebruiken om te zien wat de actuele stand van zaken is.

  // if ( typeof booking.oBookingSummary.oProductPeopleCount["P" + iProductID] === "undefined" )
  // {
  //   return [];
  // }
  //
  const aResponse = [];
  //
  // const aTimeSlotConfigs = booking.oBookingSummary.oProductPeopleCount["P" + iProductID].aTimeSlots;
  //
  // aTimeSlotConfigs.forEach((oTimeSlotConfig) => {
  //   const sLabel = oTimeSlotConfig.sLabel;
  //   const sValue = oTimeSlotConfig.sValue;
  //   //const iSpotsTotal = oTimeSlotConfig.iSpotsTotal;
  //   const iSpotsLeft = oTimeSlotConfig.iSpotsLeft;
  //
  //   if ( iSpotsLeft >= iForXNrOfPeople )
  //   {
  //     aResponse.push({
  //       sLabel: sLabel,
  //       sValue: sValue
  //     });
  //   }
  // });

  return aResponse;
}

export function areThereSlotsForThisNrOfPeople(iProductID, iOptionalNrOfPeople = 1)
{
  // const aAvailableTimeSlots = products_select.getAvailableTimeSlotsForProduct(iProductID, iOptionalNrOfPeople);
  //
  // if ( aAvailableTimeSlots.length > 0 )
  // {
  //   return true;
  // }

  return false;
}

export function getProductCatForLastProcessedDate(iProductID)
{
  iProductID = parseInt(iProductID);
  const aAvailableProductCategories = products.lAvailableTreatments;
  let iResponse = false;

  if ( aAvailableProductCategories )
  {
    aAvailableProductCategories.forEach((oCategory) => {
      const aItems = oCategory.lItems;
      if ( iResponse === false && aItems )
      {
        aItems.forEach((oItem) => {
          if ( oItem.iID === iProductID )
          {
            iResponse = parseInt(oCategory.sID, 10);
          }
        });
      }
    });
  }

  return iResponse;
}

export function isActionEntryProduct(iProductID)
{
  iProductID = parseInt(iProductID);

  const oProduct = products_select.getProductByID(iProductID);

  if (
    oProduct &&
    oProduct.bIsEntry === true &&
    products.iPreselectedActionEntryProductID !== false &&
    products.iUseThisEntryIDForFirstPerson === iProductID
  )
  {
    return true;
  }

  return false;
}

export function isNonActionEntryProduct(iProductID)
{
  iProductID = parseInt(iProductID);

  const oProduct = products_select.getProductByID(iProductID);

  if (
    oProduct &&
    oProduct.bIsEntry === true &&
    (products.iPreselectedActionEntryProductID === false || products.iUseThisEntryIDForFirstPerson !== iProductID)
  )
  {
    return true;
  }

  return false;
}

export function isEntryProduct(iProductID)
{
  iProductID = parseInt(iProductID);
  const oProduct = getProductByID(iProductID);
//console.log('isEntryProduct', iProductID, oProduct.bIsEntry, $state.snapshot(booker.aAllowedPaymentMethods));
  return oProduct && oProduct.bIsEntry === true;
}

export function isTreatmentProduct(iProductID)
{
  iProductID = parseInt(iProductID);
  const oProduct = getProductByID(iProductID);
  return oProduct && oProduct.bIsTreatment === true;
}

export function isInsuranceProduct(iProductID)
{
  iProductID = parseInt(iProductID);

  // zie ook getAppropriateInsuranceProductID
  const aInsuranceProductIDs = [61, 62, 63, 64, 65, 66, 67, 68, 69];

  return aInsuranceProductIDs.includes(iProductID);
}

export function getEntryType(iProductID = false)
{
  iProductID = parseInt(iProductID);
  if ( !iProductID || !isEntryProduct(iProductID) ) return false;

  if ( AppConfig.aEntreeAvondExternalIDs.includes(iProductID) )
  {
    return ENTRY_DURATION_EVENING;
  }

  if ( AppConfig.aEntreeOchtendExternalIDs.includes(iProductID) )
  {
    return ENTRY_DURATION_MORNING;
  }

  if ( iProductID === AppConfig.iEntree2uurExternalID )
  {
    return ENTRY_DURATION_2_HOUR;
  }

  return ENTRY_DURATION_DAY;
}

export function correctPriceIfProductHasAGodModePriceOverWriteForThisDay(iProductID, iInputPriceCents, sDate)
{
  if ( !sDate )
  {
    return;
  }
  let aTempDateParts = sDate.split('-');
  let iYear = parseInt(aTempDateParts[2]);

  if ( iProductID === 9999999999
    && iYear === 2020
    && sDate !== "01-01-2020"
    && sDate !== "02-01-2020"
    && sDate !== "03-01-2020"
    && sDate !== "04-01-2020"
    && sDate !== "05-01-2020" )
  {
    return 550;
  }

  return iInputPriceCents;
}

export function getSpecialExceptionPriceForProductForDate(iProductID, iInputBasePriceCents, sDate)
{
  iProductID = parseInt(iProductID);
  let iAlternatePriceForThisDay = false;

  const oDate = DateTimeUtil.getDateFromString(sDate);
  if ( !oDate ) return false;

  let bDateIsWeekend = oDate.getDay() === 0 || oDate.getDay() === 6;

  //const aAlternateProductPrices = AppConfig.aAlternateProductPrices;
  const aAlternateProductPrices = [];

  // todo - hier moet het mechanisme komen met de dagprijzen van entrees


  return iInputBasePriceCents;
}

